import React, { useState } from 'react';
import { BoxData } from '../models/APIModels';
import { toMoney } from '../utilities/MoneyConversion';

export interface BoxProps {
  box: BoxData;
  setBox: (box: BoxData) => BoxData;
  storageRequired: boolean;
}

// Upper bound on the typed quantity. The steppers are unbounded in practice —
// this only stops a paste of "999999999" from blowing up the totals row.
const MAX_QUANTITY = 999;

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * `/products/price` returns descriptions that lead with a parenthesised size,
 * e.g. "(46 x 46 x 46cm) Our most popular option. Useful for bedding…".
 *
 * Splitting the two halves lets the card render the size as scannable meta and
 * the remainder as prose. Both halves come from the same string the API
 * returns, so no copy is invented and nothing can contradict the catalogue —
 * which matters, because `box.dimensions` and the size quoted in the
 * description disagree on several products (Small box is 30 × 25 × 30 in the
 * copy and 30 × 25 × 40 in the columns). The copy is what Ops maintains, so
 * the copy is what we show.
 */
export function splitDescription(description?: string): {
  size?: string;
  summary?: string;
} {
  const text = collapseWhitespace(description ?? '');

  if (!text) {
    return {};
  }

  const leadingParenthetical = text.match(/^\(([^)]*)\)\s*/);

  if (!leadingParenthetical) {
    return { summary: text };
  }

  // "46 x 46 x 46cm" → "46 × 46 × 46cm". Multiplication sign, not a lowercase
  // ex — the dimensions line is the most-read part of the card.
  const size = collapseWhitespace(leadingParenthetical[1]).replace(
    /\s*x\s*/gi,
    ' × ',
  );
  const summary = collapseWhitespace(text.slice(leadingParenthetical[0].length));

  return { size: size || undefined, summary: summary || undefined };
}

/**
 * The catalogue PNGs in `public/img/keepr/` ship a WebP sibling of the same
 * name, so the card can offer one and let the browser choose.
 *
 * Only paths we know carry a sibling are rewritten — `box.imageUrl` falls back
 * to whatever the API returned (`/img/svg/…`) for any product the flow-image
 * map does not cover, and a `<source>` that 404s does NOT fall back to the
 * `<img>`, it just fails to paint.
 */
function webpSibling(imageUrl: string): string | undefined {
  return /^\/img\/keepr\/[^/]+\.png$/.test(imageUrl)
    ? imageUrl.replace(/\.png$/, '.webp')
    : undefined;
}

interface StepperProps {
  count: number;
  name: string;
  inputId: string;
  onChange: (count: number) => void;
}

/**
 * Quantity stepper. Was a pair of `<div onClick>` with an uncontrolled text
 * input wired to Formik's `handleChange` by `id`, which wrote a stray
 * top-level field (`values['1']`) and never reached `values.boxes` — so typing
 * a number did nothing at all. Now: real buttons (keyboard operable, named for
 * screen readers, 44 × 44 tap targets) and a controlled numeric input that
 * commits on every keystroke.
 */
function QuantityStepper({
  count,
  name,
  inputId,
  onChange,
}: StepperProps): JSX.Element {
  // While the field is being edited it may legitimately be empty. `draft`
  // holds that transient string; `null` means "show the committed count".
  const [draft, setDraft] = useState<string | null>(null);

  const commit = (next: number) => {
    onChange(Math.min(Math.max(next, 0), MAX_QUANTITY));
  };

  return (
    <div className="box-card__stepper">
      <button
        type="button"
        className="box-card__step"
        onClick={() => commit(count - 1)}
        disabled={count === 0}
        aria-label={`Remove one ${name}`}
      >
        <span aria-hidden="true">−</span>
      </button>
      <input
        id={inputId}
        className="box-card__quantity"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        aria-label={`Quantity of ${name}`}
        value={draft ?? String(count)}
        onChange={(event) => {
          const digits = event.target.value.replace(/[^0-9]/g, '');
          setDraft(digits);
          commit(digits === '' ? 0 : parseInt(digits, 10));
        }}
        onFocus={(event) => event.target.select()}
        onBlur={() => setDraft(null)}
      />
      <button
        type="button"
        className="box-card__step"
        onClick={() => commit(count + 1)}
        disabled={count >= MAX_QUANTITY}
        aria-label={`Add one ${name}`}
      >
        <span aria-hidden="true">+</span>
      </button>
    </div>
  );
}

export default function Box({
  box,
  setBox,
  storageRequired,
}: BoxProps): JSX.Element {
  const { size, summary } = splitDescription(box.description);

  // Storage: `purchaseCost` buys the physical box + materials, `storageCost`
  // is the per-box monthly rent. The old card showed only the first, so the
  // monthly figure in the totals row appeared out of nowhere.
  const upfrontCost = storageRequired ? box.purchaseCost : box.shippingCost;
  const monthlyCost = box.storageCost;
  const selected = box.count > 0;
  const webpSource = webpSibling(box.imageUrl);

  return (
    <div className={`box-card${selected ? ' box-card--selected' : ''}`}>
      <div className="box-card__media">
        {/*
          Eager, not lazy. The nine catalogue images were 2918 × 3126 PNGs —
          3.5 MB for a page that never renders one taller than 72 px — so
          they trickled in well after the text. Resampled to 400 px with a
          WebP sibling they total ~100 KB, at which point deferring them only
          delays the paint the customer is waiting on.
        */}
        <picture>
          {webpSource && <source srcSet={webpSource} type="image/webp" />}
          <img
            className="box-card__image"
            src={box.imageUrl}
            alt=""
            decoding="async"
          />
        </picture>
        {selected && (
          <span className="box-card__count" aria-hidden="true">
            {box.count}
          </span>
        )}
      </div>

      <div className="box-card__body">
        <h3 className="box-card__name">{box.name}</h3>

        <p className="box-card__pricing">
          <span className="box-card__price">
            <span className="box-card__price-value">
              {upfrontCost > 0 ? toMoney(upfrontCost) : 'Free'}
            </span>
            <span className="box-card__price-unit">one-off</span>
          </span>
          {storageRequired && monthlyCost > 0 && (
            <span className="box-card__monthly">
              {toMoney(monthlyCost)} a month
            </span>
          )}
        </p>

        {size && <p className="box-card__size">{size}</p>}
        {summary && <p className="box-card__summary">{summary}</p>}
      </div>

      <QuantityStepper
        count={box.count}
        name={box.name}
        inputId={`box-quantity-${box.id}`}
        onChange={(count) => setBox({ ...box, count })}
      />

    </div>
  );
}
